import { useEffect, useMemo, useState } from 'react';
import './LandingPage.css';

export default function ContributionsTab({
  data,
  loading,
  error,
  totals,
  onRetry,
  onSubmit,
  history,
  historyLoading,
  historyError,
  historyPage = 1,
  onHistoryRetry,
  onHistoryPageChange,
  onUpdateContribution,
  onDeleteContribution,
}) {
  const [formState, setFormState] = useState({
    member_id: '',
    member_name: '',
    amount: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isNonMember, setIsNonMember] = useState(false);

  const [editState, setEditState] = useState({ id: null, amount: '', note: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editFeedback, setEditFeedback] = useState({ error: '', success: '' });
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const members = data?.members ?? [];
  const historyEntries = history?.contributions ?? [];
  const historyPagination = history?.pagination ?? {
    page: historyPage,
    per_page: history?.pagination?.per_page ?? 100,
    total_pages: history?.pagination?.total_pages ?? 1,
    total_contributions: history?.pagination?.total_contributions ?? historyEntries.length,
  };

  useEffect(() => {
    if (editState.id && !historyEntries.some((entry) => entry.id === editState.id)) {
      setEditState({ id: null, amount: '', note: '' });
    }
  }, [historyEntries, editState.id]);

  const totalsDisplay = useMemo(
    () => Number(totals?.total_collected || 0),
    [totals]
  );

  const selectableMembers = useMemo(
    () =>
      members.filter((member) => Number(member.total_collected ?? 0) <= 0),
    [members]
  );

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString()}`;

  const contributorCount = useMemo(
    () => members.filter((member) => Number(member.total_collected ?? 0) > 0).length,
    [members]
  );

  const totalMemberCollected = useMemo(
    () => members.reduce((acc, member) => acc + Number(member.total_collected ?? 0), 0),
    [members]
  );

  const handleExportMembers = () => {
    if (!members.length) {
      return;
    }

    const headers = ['Member', 'Phone', 'Age', 'Total collected', 'Last contribution'];
    const rows = members.map((member) => [
      member.name ?? '',
      member.mobile ?? '',
      member.age ?? '',
      Number(member.total_collected ?? 0),
      formatDateTime(member.last_contribution),
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'unit-members.csv';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShareWhatsApp = () => {
    if (!members.length) {
      return;
    }

    const dateString = new Date().toLocaleString('en-IN');
    const unitTotal = formatCurrency(totals?.total_collected ?? 0);

    const body = members
      .map((member, index) => {
        const last = member.last_contribution ? formatDateTime(member.last_contribution) : '—';
        return [
          `${index + 1}. ${member.name ?? 'Unnamed'} (ID: ${member.member_id})`,
          `   Phone: ${member.mobile || '—'}`,
          `   Age: ${member.age ?? '—'}`,
          `   Collected: ${formatCurrency(member.total_collected)}`,
          `   Last: ${last}`,
        ].join('\n');
      })
      .join('\n\n');

    const header = `Unit Contribution Summary`;
    const summary = `Unit total collected: ${unitTotal}\nGenerated: ${dateString}\nMembers listed: ${members.length}`;

    const message = [header, summary, body || 'No members available'].join('\n\n');
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const resetEditState = () => {
    setEditState({ id: null, amount: '', note: '' });
  };

  const startEdit = (entry) => {
    setEditFeedback({ error: '', success: '' });
    setEditState({
      id: entry.id,
      amount: entry.amount !== undefined && entry.amount !== null ? String(entry.amount) : '',
      note: entry.note ?? '',
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editState.id) {
      return;
    }
    if (!editState.amount) {
      setEditFeedback({ error: 'Enter amount', success: '' });
      return;
    }
    if (!onUpdateContribution) {
      setEditFeedback({ error: 'Update unavailable', success: '' });
      return;
    }

    setEditLoading(true);
    setEditFeedback({ error: '', success: '' });
    try {
      await onUpdateContribution({
        id: editState.id,
        amount: Number(editState.amount),
        note: editState.note,
      });
      setEditFeedback({ error: '', success: 'Contribution updated' });
      resetEditState();
    } catch (err) {
      setEditFeedback({ error: err.message || 'Unable to update contribution', success: '' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!onDeleteContribution) {
      setEditFeedback({ error: 'Delete unavailable', success: '' });
      return;
    }
    const confirmDelete = window.confirm('Delete this contribution record?');
    if (!confirmDelete) {
      return;
    }

    setEditFeedback({ error: '', success: '' });
    setDeleteLoadingId(entry.id);
    try {
      await onDeleteContribution(entry.id);
      setEditFeedback({ error: '', success: 'Contribution deleted' });
      if (editState.id === entry.id) {
        resetEditState();
      }
    } catch (err) {
      setEditFeedback({ error: err.message || 'Unable to delete contribution', success: '' });
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleHistoryPrev = () => {
    if (historyPagination.page > 1 && onHistoryPageChange) {
      onHistoryPageChange(historyPagination.page - 1, historyPagination.per_page);
    }
  };

  const handleHistoryNext = () => {
    if (historyPagination.page < (historyPagination.total_pages ?? 1) && onHistoryPageChange) {
      onHistoryPageChange(historyPagination.page + 1, historyPagination.per_page);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleNonMember = (event) => {
    const checked = event.target.checked;
    setIsNonMember(checked);
    setFormState((prev) =>
      checked
        ? { ...prev, member_id: '', member_name: '' }
        : { ...prev, member_name: '' }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    if (isNonMember) {
      if (!formState.member_name || !formState.amount) {
        setSubmitError('Enter name and amount');
        return;
      }
    } else {
      if (!formState.member_id || !formState.amount) {
        setSubmitError('Select member and amount');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        amount: Number(formState.amount),
      };
      
      if (isNonMember) {
        payload.member_name = formState.member_name;
        payload.note = `Non-member contribution - ${formState.member_name}`;
      } else {
        payload.member_id = Number(formState.member_id);
      }
      
      await onSubmit(payload);

      setFormState({
        member_id: '',
        member_name: '',
        amount: '',
      });
      setIsNonMember(false);
      setSubmitSuccess('Contribution saved');
    } catch (err) {
      setSubmitError(err.message || 'Unable to save contribution');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contrib-wrapper">
      <div className="contrib-header">
        <div className="totals-pill">
          <span>Total collected:</span>
          <strong>₹{totalsDisplay.toLocaleString()}</strong>
        </div>
      </div>

      <section className="contrib-form-card">
        <h2>Add contribution</h2>
        <div style={{marginBottom: '1rem'}}>
          <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <input
              type="checkbox"
              checked={isNonMember}
              onChange={handleToggleNonMember}
            />
            Non-member contribution
          </label>
        </div>
        <form className="contrib-form" onSubmit={handleSubmit}>
          {!isNonMember ? (
            <label>
              Member
              <select
                name="member_id"
                value={formState.member_id}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="">Select member</option>
                {selectableMembers.map((member) => (
                  <option key={member.member_id} value={member.member_id}>
                    {member.name} ({member.member_id})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label>
              Name
              <input
                type="text"
                name="member_name"
                value={formState.member_name || ''}
                onChange={handleInputChange}
                disabled={submitting}
                placeholder="Enter person's name"
              />
            </label>
          )}

          <label>
            Amount
            <input
              type="number"
              min="0"
              step="0.01"
              name="amount"
              value={formState.amount}
              onChange={handleInputChange}
              disabled={submitting}
            />
          </label>

          <div>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save contribution'}
            </button>
          </div>
        </form>
        {submitError && <div className="inline-message error">{submitError}</div>}
        {submitSuccess && <div className="inline-message success">{submitSuccess}</div>}
      </section>

      <section className="contrib-history-section">
        <div className="contrib-history-header">
          <h2>Contribution records</h2>
          <p>
            Review contributions and make quick edits or deletions in one place.
          </p>
          <div className="contrib-history-meta">
            <span>
              Total records: {historyPagination.total_contributions ?? historyEntries.length}
            </span>
            {onHistoryPageChange && historyPagination.total_pages > 1 && (
              <div className="contrib-history-pagination">
                <button
                  type="button"
                  className="btn outline"
                  onClick={handleHistoryPrev}
                  disabled={historyLoading || historyPagination.page <= 1}
                >
                  Prev
                </button>
                <span>
                  Page {historyPagination.page} of {historyPagination.total_pages || 1}
                </span>
                <button
                  type="button"
                  className="btn outline"
                  onClick={handleHistoryNext}
                  disabled={
                    historyLoading ||
                    historyPagination.page >= (historyPagination.total_pages || 1)
                  }
                >
                  Next
                </button>
              </div>
            )}
          </div>
          <div className="contrib-history-actions util-actions">
            <button type="button" className="btn outline" onClick={handleExportMembers}>
              Export members CSV
            </button>
            <button type="button" className="btn outline" onClick={handleShareWhatsApp}>
              Share member list
            </button>
          </div>
        </div>

        {historyLoading ? (
          <div className="inline-message">Loading history…</div>
        ) : historyError ? (
          <div className="inline-message error">
            <span>{historyError}</span>
            {onHistoryRetry && (
              <button type="button" className="btn outline" onClick={onHistoryRetry}>
                Retry
              </button>
            )}
          </div>
        ) : (
          <div className="contrib-history-table-wrapper">
            <table className="contrib-history-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Amount</th>
                  <th>Note</th>
                  <th>Recorded at</th>
                  <th>Recorded by</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyEntries.length ? (
                  historyEntries.map((entry) => {
                    const isEditing = editState.id === entry.id;
                    return (
                      <tr key={entry.id}>
                        <td>
                          <div className="contrib-history-member">
                            <span className="name">{entry.member_name ?? '–'}</span>
                            <span className="muted">ID: {entry.member_id}</span>
                          </div>
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              name="amount"
                              value={editState.amount}
                              onChange={handleEditChange}
                              disabled={editLoading}
                            />
                          ) : (
                            formatCurrency(entry.amount)
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              name="note"
                              value={editState.note}
                              onChange={handleEditChange}
                              disabled={editLoading}
                              placeholder="Optional note"
                            />
                          ) : (
                            entry.note ? entry.note : '—'
                          )}
                        </td>
                        <td>{formatDateTime(entry.recorded_at)}</td>
                        <td>{entry.recorded_by_name || '—'}</td>
                        <td className="contrib-history-actions">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="btn primary"
                                onClick={handleEditSubmit}
                                disabled={editLoading}
                              >
                                {editLoading ? 'Updating…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="btn outline"
                                onClick={resetEditState}
                                disabled={editLoading}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn outline"
                                onClick={() => startEdit(entry)}
                                disabled={editLoading || deleteLoadingId !== null}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn danger"
                                onClick={() => handleDelete(entry)}
                                disabled={deleteLoadingId === entry.id || editLoading}
                              >
                                {deleteLoadingId === entry.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="empty-state">
                      No contribution records found for this unit.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {editFeedback.error && <div className="inline-message error">{editFeedback.error}</div>}
        {editFeedback.success && <div className="inline-message success">{editFeedback.success}</div>}
      </section>
    </div>
  );
}
